/**
 * Footer Component - WorkStream
 * Rodapé reutilizável para o marketing site
 */
import { Mail, Phone, MapPin, Twitter, Linkedin, Instagram, Youtube, Zap } from 'lucide-react'

export default function Footer() {
  const currentYear = new Date().getFullYear()

  const footerLinks = {
    servicos: [
      { label: 'Ponto Facial', href: '/#services' },
      { label: 'Produtividade', href: '/#services' },
      { label: 'IA Comportamental', href: '/#services' },
      { label: 'Segurança & EPI', href: '/#services' },
    ],
    segmentos: [
      { label: 'Construção Civil', href: '/#segments' },
      { label: 'Telecomunicações', href: '/#segments' },
      { label: 'Hospitais', href: '/#segments' },
      { label: 'Indústria', href: '/#segments' },
    ],
    recursos: [
      { label: 'Documentação', href: '/docs' },
      { label: 'API', href: '/api' },
      { label: 'Suporte', href: '/support' },
      { label: 'Status', href: 'https://status.workstream.com.br' },
    ],
    legal: [
      { label: 'Privacidade', href: '/privacy' },
      { label: 'Termos de Uso', href: '/terms' },
      { label: 'LGPD', href: '/lgpd' },
      { label: 'Compliance', href: '/compliance' },
    ],
  }

  const socialLinks = [
    { icon: Twitter, href: 'https://twitter.com/workstream', label: 'Twitter' },
    { icon: Linkedin, href: 'https://linkedin.com/company/workstream', label: 'LinkedIn' },
    { icon: Instagram, href: 'https://instagram.com/workstream', label: 'Instagram' },
    { icon: Youtube, href: 'https://youtube.com/workstream', label: 'YouTube' },
  ]

  return (
    <footer className="bg-[#0B1120] border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-12">
          {/* Brand Column */}
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)' }}
              >
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-black text-white">
                Work<span className="font-light italic text-cyan-400">Stream</span>
              </span>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Visão que move o trabalho. Monitoramento inteligente com visão computacional e IA.
            </p>
            <div className="space-y-2 text-sm">
              <a href="mailto:contato@workstream.com.br" className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors">
                <Mail className="h-4 w-4" />
                contato@workstream.com.br
              </a>
              <a href="tel:+5511300000000" className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors">
                <Phone className="h-4 w-4" />
                +55 (11) 3000-0000
              </a>
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="h-4 w-4" />
                São Paulo, SP - Brasil
              </div>
            </div>
          </div>

          {/* Serviços */}
          <div>
            <h3 className="text-white font-semibold mb-4">Serviços</h3>
            <ul className="space-y-2 text-sm">
              {footerLinks.servicos.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-slate-400 hover:text-cyan-400 transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Segmentos */}
          <div>
            <h3 className="text-white font-semibold mb-4">Segmentos</h3>
            <ul className="space-y-2 text-sm">
              {footerLinks.segmentos.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-slate-400 hover:text-cyan-400 transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Recursos */}
          <div>
            <h3 className="text-white font-semibold mb-4">Recursos</h3>
            <ul className="space-y-2 text-sm">
              {footerLinks.recursos.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-slate-400 hover:text-cyan-400 transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-2 text-sm">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-slate-400 hover:text-cyan-400 transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-800 my-8"></div>

        {/* Bottom Footer */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500">
            © {currentYear} WorkStream. Todos os direitos reservados.
          </p>

          {/* Social Links */}
          <div className="flex gap-4">
            {socialLinks.map((social) => {
              const IconComponent = social.icon
              return (
                <a
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg text-slate-500 hover:text-cyan-400 hover:bg-slate-800 transition-all"
                  aria-label={social.label}
                >
                  <IconComponent className="h-5 w-5" />
                </a>
              )
            })}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 pt-8 border-t border-slate-800">
          <div className="flex flex-wrap justify-center gap-6 text-xs text-slate-500">
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/50">
              🔒 SSL 256-bit
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/50">
              ✅ LGPD Compliant
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/50">
              🇧🇷 100% Brasileiro
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/50">
              ⚡ 99.9% Uptime
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/50">
              🧠 Powered by AI
            </span>
          </div>
        </div>
      </div>
    </footer>
  )
}
